import { ReactNode, useState, useEffect } from "react";
import Modal from "react-modal";

import { TabHeader, Tab } from "components/TabHeader";

import Close from "assets/close-small.svg";
import { isIncludedTab } from "utils/isDesactivatedTab";
import { useAuth } from "contexts/authContext";
import { ContentElement, Footer, Header, ModalOverlay, Button } from "./styles";
import { getReportDocx, getReportPdf } from "services/ReportServices";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { IOccurrence } from "interfaces/Occurrence";
import { ReportTypeModal } from "./ReportTypeModal";

type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;

  /** Conteúdo do modal */
  children: ReactNode;

  /** Lista das tabs que serão exibidas */
  tabs: Tab[];

  /** Tab que está ativa no momento */
  selectedTab: Tab;

  /** Lista com as tabs que serão exibidas em vermelho (...que possuem erros) */
  invalidTabs: Tab[];

  /** Lista com as tabs que não serão exibidas(...que por alguma logica nao deverão aparecer) */
  deactivatedTabs: Tab[];

  /** Função que envia a tab selecionada pelos botões de anterior e próximo */
  setSelectedTab: (tab: Tab) => void;

  /** Título que será exibido na modal */
  title: string;

  /** Função de submit que será executada pelo botão laranja do footer */
  handleSubmit: () => void;

  /** Flag para saber se os dados do formulário estão sendo enviados */
  sending: boolean;

  /** Texto que será exibido dentro do botão de envio (laranja) */
  buttonText: string;

  /**Remover os botões de Anterior e Proximo se o modal só tiver uma tab */
  isOneTabModal?: boolean;

  /**Desativar Botão de ação sem removelo */
  disableButton?: boolean;

  /** Remove o botão de submit do footer, se o modal não precisar de um */
  removeSubmitButton?: boolean;

  modalClassname?: string;

  hideFooter?: boolean;
};

function StepModalTemplate({
  opened,
  setOpened,
  tabs,
  children,
  invalidTabs,
  selectedTab,
  setSelectedTab,
  title,
  handleSubmit,
  buttonText,
  sending,
  deactivatedTabs,
  modalClassname,
  isOneTabModal,
  removeSubmitButton = false,
  disableButton,
  hideFooter = false,
}: Props) {
  const [isReportTypeModalOpen, setIsReportTypeModalOpen] = useState(false);

  const { config } = useAuth();
  const { occurrenceToEdit } = useManualOccurrence();
  function onRequestClose() {
    setOpened(false);
  }

  function onTabClick(_tab: Tab) {
    setSelectedTab(_tab);
  }
  function previousTab() {
    if (selectedTab.index === 0) return;
    setSelectedTab(tabs[selectedTab.index - 1]);
  }

  function nextTab() {
    if (selectedTab.index === tabs.length) return;
    setSelectedTab(tabs[selectedTab.index + 1]);
  }

  async function handleExportPdfReport(occurrence: IOccurrence) {
    if (!occurrence.report) return;

    getReportPdf(occurrence.id)
      .then(async (res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => { });
  }

  async function handleExportWordReport(occurrence: IOccurrence) {
    if (!occurrence.report) return;

    getReportDocx(occurrence.id)
      .then(async (res) => {
        const fileURL = URL.createObjectURL(res.data);
        const link = document.createElement("a");

        link.href = fileURL;
        link.setAttribute("download", `occurrence-${occurrence.protocol}.docx`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(fileURL);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => { });
  }

  useEffect(() => {
    console.log({ isReportTypeModalOpen });
  }, [isReportTypeModalOpen]);

  return (
    <Modal
      isOpen={opened}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={false}
      contentElement={(props, children) => (
        <ContentElement {...props}>{children}</ContentElement>
      )}
      overlayElement={(props, contentElement) => (
        <ModalOverlay {...props}>{contentElement}</ModalOverlay>
      )}
    >
      <ReportTypeModal
        isOpen={isReportTypeModalOpen}
        occurrence={occurrenceToEdit}
        closeModal={() => setIsReportTypeModalOpen(false)}
        onPdfButtonClick={handleExportPdfReport}
        onWordButtonClick={handleExportWordReport}
      />

      <Header>
        <div className="titleContainer">
          <div className="titleContainer_text">
            <h2>{title}</h2>

            {occurrenceToEdit?.status === "concluido" && (
              <Button
                type="button"
                onClick={() => setIsReportTypeModalOpen(true)}
              >
                Exportar Relatório
              </Button>
            )}
          </div>
          <img
            src={Close}
            alt="Fechar modal"
            onClick={onRequestClose}
            data-cy="close-modal-template"
          />
        </div>

        <TabHeader
          tabs={tabs}
          invalidTabs={invalidTabs}
          onTabClick={onTabClick}
          selectedTab={selectedTab.index}
          deactivatedTabs={deactivatedTabs}
        />
      </Header>

      {children}

      {!hideFooter && !isOneTabModal && (
        <Footer>
          <button
            disabled={selectedTab.index === 0}
            onClick={() => previousTab()}
          >
            Anterior
          </button>

          <div>
            {!removeSubmitButton && (
              <button
                style={{
                  backgroundColor: config?.color || "#ff8f01",
                }}
                disabled={
                  disableButton ||
                  sending ||
                  !(
                    selectedTab.index === tabs.length - 1 ||
                    isIncludedTab(tabs[selectedTab.index + 1], deactivatedTabs)
                  )
                }
                className="saveButton"
                onClick={() => handleSubmit()}
              >
                {buttonText}
              </button>
            )}

            <button
              disabled={
                selectedTab.index === tabs.length - 1 ||
                isIncludedTab(tabs[selectedTab.index + 1], deactivatedTabs)
              }
              onClick={() => nextTab()}
            >
              Próximo
            </button>
          </div>
        </Footer>
      )}
      {!hideFooter && isOneTabModal && (
        <Footer>
          <button
            style={{
              backgroundColor: disableButton
                ? "grey"
                : config?.color || "#ff8f01",
            }}
            disabled={disableButton || sending}
            className="oneTabSaveButton"
            onClick={() => handleSubmit()}
          >
            {buttonText}
          </button>
        </Footer>
      )}
    </Modal>
  );
}

export { StepModalTemplate };
